import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { ParsedContact } from '@/types/contact';

export interface FieldPreset {
  id: string;
  user_id: string;
  name: string;
  description?: string;
  fields: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface RequiredFieldsResult {
  requiredFields: string[];
  missingFields: string[];
  isValid: boolean;
  isLoading: boolean;
}

// Alle verfügbaren Felder (gruppiert)
export const AVAILABLE_FIELDS = {
  personal: [
    { value: 'title', label: 'Title (Dr., Prof.)' },
    { value: 'givenName', label: 'First Name' },
    { value: 'middleName', label: 'Middle Name' },
    { value: 'surname', label: 'Last Name' },
    { value: 'suffix', label: 'Suffix (Jr., Sr.)' },
    { value: 'nickname', label: 'Nickname' },
    { value: 'yomiGivenName', label: 'Given Name (Yomi)' },
    { value: 'yomiSurname', label: 'Surname (Yomi)' },
    { value: 'generation', label: 'Generation' },
    { value: 'birthday', label: 'Birthday' },
    { value: 'anniversary', label: 'Anniversary' },
    { value: 'spouseName', label: 'Spouse' },
    { value: 'schools', label: 'Schools' },
    { value: 'hobby', label: 'Hobby' },
    { value: 'location', label: 'Location' },
  ],
  business: [
    { value: 'companyName', label: 'Company Name' },
    { value: 'yomiCompanyName', label: 'Company Name (Yomi)' },
    { value: 'jobTitle', label: 'Job Title' },
    { value: 'department', label: 'Department' },
    { value: 'profession', label: 'Profession' },
    { value: 'officeLocation', label: 'Office Location' },
    { value: 'manager', label: 'Manager' },
    { value: 'assistantName', label: 'Assistant Name' },
    { value: 'assistantPhone', label: 'Assistant Phone' },
  ],
  contact: [
    { value: 'emailAddresses', label: 'Email Addresses' },
    { value: 'mobilePhone', label: 'Mobile Phone' },
    { value: 'businessPhones', label: 'Business Phones' },
    { value: 'homePhones', label: 'Home Phones' },
    { value: 'carPhone', label: 'Car Phone' },
    { value: 'pager', label: 'Pager' },
    { value: 'callback', label: 'Callback' },
    { value: 'radioPhone', label: 'Radio Phone' },
    { value: 'ttytddPhone', label: 'TTY/TDD Phone' },
    { value: 'telex', label: 'Telex' },
    { value: 'companyMainPhone', label: 'Company Main Phone' },
    { value: 'businessFax', label: 'Business Fax' },
    { value: 'homeFax', label: 'Home Fax' },
    { value: 'otherFax', label: 'Other Fax' },
    { value: 'imAddresses', label: 'IM Addresses' },
    { value: 'businessHomePage', label: 'Business Website' },
    { value: 'personalWebPage', label: 'Personal Website' },
  ],
  address: [
    { value: 'businessAddress.street', label: 'Business Street' },
    { value: 'businessAddress.city', label: 'Business City' },
    { value: 'businessAddress.postalCode', label: 'Business Postal Code' },
    { value: 'businessAddress.state', label: 'Business State' },
    { value: 'businessAddress.countryOrRegion', label: 'Business Country' },
    { value: 'homeAddress.street', label: 'Home Street' },
    { value: 'homeAddress.city', label: 'Home City' },
    { value: 'homeAddress.postalCode', label: 'Home Postal Code' },
    { value: 'homeAddress.state', label: 'Home State' },
    { value: 'homeAddress.countryOrRegion', label: 'Home Country' },
    { value: 'otherAddress.street', label: 'Other Street' },
    { value: 'otherAddress.city', label: 'Other City' },
    { value: 'otherAddress.postalCode', label: 'Other Postal Code' },
    { value: 'otherAddress.state', label: 'Other State' },
    { value: 'otherAddress.countryOrRegion', label: 'Other Country' },
  ],
};

export const useRequiredFields = () => {
  const [requiredFields, setRequiredFields] = useState<string[]>([]);
  const [presets, setPresets] = useState<FieldPreset[]>([]);
  const [activePreset, setActivePreset] = useState<FieldPreset | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [enableFieldPresets, setEnableFieldPresets] = useState(true);

  useEffect(() => {
    migrateAndLoadPresets();
  }, []);

  const migrateAndLoadPresets = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setIsLoading(false);
        return;
      }

      // Check if user has old required fields but no presets
      const { data: oldFields } = await supabase
        .from('user_required_fields')
        .select('field_name')
        .eq('user_id', user.id);

      const { data: existingPresets } = await supabase
        .from('user_field_presets')
        .select('id')
        .eq('user_id', user.id);

      // If has old fields but no presets → migrate
      if (oldFields && oldFields.length > 0 && (!existingPresets || existingPresets.length === 0)) {
        const { data, error } = await supabase
          .from('user_field_presets')
          .insert({
            user_id: user.id,
            name: 'My Custom Fields',
            description: 'Migrated from previous settings',
            fields: oldFields.map(f => f.field_name),
            is_active: true,
          })
          .select()
          .single();

        if (error) {
          console.error('Error creating migration preset:', error);
        }
      }
    } catch (error) {
      console.error('Error migrating old required fields:', error);
    }
    
    await loadPresets();
  };

  const loadPresets = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Load enable_field_presets setting
      const { data: settings } = await supabase
        .from('user_settings')
        .select('enable_field_presets')
        .eq('user_id', user.id)
        .maybeSingle();

      setEnableFieldPresets(settings?.enable_field_presets ?? true);

      const { data, error } = await supabase
        .from('user_field_presets')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      setPresets(data || []);
      
      const active = data?.find(p => p.is_active);
      if (active) {
        setActivePreset(active);
        const shouldEnableFields = settings?.enable_field_presets !== false;
        console.log('[useRequiredFields] Setting required fields:', {
          shouldEnableFields,
          settingsValue: settings?.enable_field_presets,
          activePresetFields: active.fields,
          willSetFields: shouldEnableFields ? active.fields : []
        });
        setRequiredFields(shouldEnableFields ? active.fields : []);
      } else {
        setActivePreset(null);
        setRequiredFields([]);
      }
    } catch (error) {
      console.error('Error loading presets:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const createPreset = async (name: string, description: string, fields: string[]) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('user_field_presets')
        .insert({
          user_id: user.id,
          name,
          description,
          fields,
          is_active: false,
        })
        .select()
        .single();

      if (error) throw error;
      
      await loadPresets();
      return { success: true, preset: data };
    } catch (error) {
      console.error('Error creating preset:', error);
      return { success: false, error };
    }
  };

  const updatePreset = async (presetId: string, updates: Partial<FieldPreset>) => {
    try {
      const { error } = await supabase
        .from('user_field_presets')
        .update(updates)
        .eq('id', presetId);

      if (error) throw error;
      
      await loadPresets();
      return true;
    } catch (error) {
      console.error('Error updating preset:', error);
      return false;
    }
  };

  const deletePreset = async (presetId: string) => {
    try {
      const { error } = await supabase
        .from('user_field_presets')
        .delete()
        .eq('id', presetId);

      if (error) throw error;
      
      await loadPresets();
      return true;
    } catch (error) {
      console.error('Error deleting preset:', error);
      return false;
    }
  };

  const activatePreset = async (presetId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('user_field_presets')
        .update({ is_active: false })
        .eq('user_id', user.id);

      if (error) throw error;

      const { error: activateError } = await supabase
        .from('user_field_presets')
        .update({ is_active: true })
        .eq('id', presetId);

      if (activateError) throw activateError;
      
      await loadPresets();
      return true;
    } catch (error) {
      console.error('Error activating preset:', error);
      return false;
    }
  };

  const toggleFieldPresets = async (enabled: boolean) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('user_settings')
        .upsert({
          user_id: user.id,
          enable_field_presets: enabled,
        }, {
          onConflict: 'user_id'
        });

      if (error) throw error;

      setEnableFieldPresets(enabled);
      
      if (enabled && activePreset) {
        setRequiredFields(activePreset.fields);
      } else {
        setRequiredFields([]);
      }
      
      return true;
    } catch (error) {
      console.error('Error toggling field presets:', error);
      return false;
    }
  };

  const validateContact = (contact: ParsedContact): RequiredFieldsResult => {
    const missingFields: string[] = [];
    
    requiredFields.forEach(fieldPath => {
      const parts = fieldPath.split('.');
      let value: any = contact;
      
      for (const part of parts) {
        value = value?.[part];
      }
      
      const hasValue = value !== undefined && 
                       value !== null && 
                       value !== '' && 
                       !(Array.isArray(value) && value.length === 0);
      
      if (!hasValue) {
        missingFields.push(fieldPath);
      }
    });

    return {
      requiredFields,
      missingFields,
      isValid: missingFields.length === 0,
      isLoading: false,
    };
  };

  const syncWithSmartFeatures = async (smartNotesEnabled: boolean, smartCategoriesEnabled: boolean) => {
    if (!smartNotesEnabled && !smartCategoriesEnabled && activePreset) {
      const { error } = await supabase
        .from('user_field_presets')
        .update({ is_active: false })
        .eq('id', activePreset.id);
      
      if (!error) {
        await loadPresets();
      }
    }
  };

  return {
    requiredFields,
    presets,
    activePreset,
    isLoading,
    enableFieldPresets,
    createPreset,
    updatePreset,
    deletePreset,
    activatePreset,
    toggleFieldPresets,
    loadPresets,
    validateContact,
    syncWithSmartFeatures,
  };
};
